import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Truck, Phone, Mail, CheckCircle, XCircle, Clock, Search, MapPin, Navigation, Radio, Map, Info, BarChart3, Star, Camera, Eye } from 'lucide-react';
import apiService from '../services/api';
import Modal from './Modal';
import LivreursMap from './LivreursMap';
import LivreurPhotoUpload from './LivreurPhotoUpload';
import LivreurAccountPanel from './LivreurAccountPanel';

const LivreursManager = ({ commercantId, commercant, onCommercantUpdate }) => {
  const [livreurs, setLivreurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedLivreur, setSelectedLivreur] = useState(null);
  const [savingToggle, setSavingToggle] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    vehicule: '',
    actif: true,
    disponible: true,
    statut: 'disponible',
    photo: '',
    rayonMaxKm: null
  });

  // État pour le toggle livraisonSuiviGPS
  const livraisonSuiviGPS = commercant?.livraisonSuiviGPS !== undefined ? commercant.livraisonSuiviGPS : true;

  useEffect(() => {
    fetchLivreurs();
  }, [commercantId]);

  const fetchLivreurs = async () => {
    try {
      setLoading(true);
      // Utiliser l'endpoint spécifique par commerçant
      const data = await apiService.getLivreurs(commercantId);
      // L'API retourne directement les livreurs du commerçant
      const livreursCommercant = Array.isArray(data) ? data : [];
      setLivreurs(livreursCommercant);
      setError('');
    } catch (err) {
      setError('Erreur lors du chargement des livreurs');
      console.error('Livreurs error:', err);
      setLivreurs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLivraisonSuiviGPS = async () => {
    if (!commercant) return;
    
    setSavingToggle(true);
    try {
      await apiService.updateCommercant(commercantId, {
        livraisonSuiviGPS: !livraisonSuiviGPS
      });
      if (onCommercantUpdate) {
        await onCommercantUpdate();
      }
    } catch (err) {
      setError('Erreur lors de la mise à jour du paramètre');
      console.error('Toggle error:', err);
    } finally {
      setSavingToggle(false);
    }
  };

  const filteredLivreurs = livreurs.filter(livreur =>
    searchTerm === '' ||
    livreur.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    livreur.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    livreur.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    livreur.vehicule?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccessMessage('');
      if (selectedLivreur) {
        const { actif: _actif, ...profileData } = formData;
        await apiService.updateLivreur(selectedLivreur.id, profileData);
      } else {
        await apiService.createLivreur({ ...formData, commercantId });
        setSuccessMessage('Livreur créé. Un email d\'activation PWA a été envoyé.');
      }
      setShowModal(false);
      setSelectedLivreur(null);
      resetForm();
      fetchLivreurs();
    } catch (err) {
      setError(err.message || 'Erreur lors de la sauvegarde');
      console.error('Save error:', err);
    }
  };

  const handleEdit = (livreur) => {
    setSelectedLivreur(livreur);
    setFormData({
      nom: livreur.nom || '',
      prenom: livreur.prenom || '',
      email: livreur.email || '',
      telephone: livreur.telephone || '',
      vehicule: livreur.vehicule || '',
      actif: livreur.actif ?? true,
      disponible: livreur.disponible ?? true,
      statut: livreur.statut || 'disponible',
      photo: livreur.photo || '',
      rayonMaxKm: livreur.rayonMaxKm || null
    });
    setShowModal(true);
  };

  const handleDelete = async (livreur) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le livreur ${livreur.nom} ${livreur.prenom} ?`)) {
      return;
    }

    try {
      await apiService.deleteLivreur(livreur.id);
      fetchLivreurs();
    } catch (err) {
      setError('Erreur lors de la suppression');
      console.error('Delete error:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      vehicule: '',
      actif: true,
      disponible: true,
      statut: 'disponible',
      photo: '',
      rayonMaxKm: null
    });
  };

  const getStatusIcon = (statut, disponible, actif) => {
    if (!actif) return <XCircle className="w-4 h-4 text-gray-500" />;
    
    switch (statut) {
      case 'en_livraison':
      case 'en_route':
        return <Clock className="w-4 h-4 text-orange-500" />;
      case 'au_restaurant':
        return <MapPin className="w-4 h-4 text-blue-500" />;
      case 'arrive':
        return <Navigation className="w-4 h-4 text-purple-500" />;
      case 'pause':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return disponible 
          ? <CheckCircle className="w-4 h-4 text-green-500" /> 
          : <XCircle className="w-4 h-4 text-red-500" />;
    }
  };

  const getStatusLabel = (statut, disponible, actif) => {
    if (!actif) return 'Inactif';
    
    switch (statut) {
      case 'en_livraison':
        return 'En livraison';
      case 'au_restaurant':
        return 'Au restaurant';
      case 'en_route':
        return 'En route';
      case 'arrive':
        return 'Arrivé';
      case 'pause':
        return 'Pause';
      default:
        return disponible ? 'Disponible' : 'Indisponible';
    }
  };

  const getStatusColor = (statut, disponible, actif) => {
    if (!actif) return 'bg-gray-100 text-gray-800';
    
    switch (statut) {
      case 'en_livraison':
      case 'en_route':
        return 'bg-orange-100 text-orange-800';
      case 'au_restaurant':
        return 'bg-blue-100 text-blue-800';
      case 'arrive':
        return 'bg-purple-100 text-purple-800';
      case 'pause':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return disponible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
    }
  };

  const getStatutOptions = () => [
    { value: 'disponible', label: 'Disponible' },
    { value: 'en_livraison', label: 'En livraison' },
    { value: 'au_restaurant', label: 'Au restaurant' },
    { value: 'en_route', label: 'En route' },
    { value: 'arrive', label: 'Arrivé' },
    { value: 'pause', label: 'Pause' }
  ];

  const getVehiculeOptions = () => [
    { value: '', label: 'Sélectionner...' },
    { value: 'Vélo', label: 'Vélo' },
    { value: 'Scooter', label: 'Scooter' },
    { value: 'Moto', label: 'Moto' },
    { value: 'Voiture', label: 'Voiture' },
    { value: 'Camionnette', label: 'Camionnette' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec toggle livraisonSuiviGPS */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Gestion des Livreurs</h2>
            <p className="text-sm text-gray-600 mt-1">Gérer les livreurs assignés à ce commerçant</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setSelectedLivreur(null);
              setShowModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Ajouter un livreur
          </button>
        </div>

        {/* Toggle Suivi GPS/Maps */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Map className="w-5 h-5 text-gray-600" />
                <h3 className="text-sm font-semibold text-gray-900">Suivi GPS et Maps</h3>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {livraisonSuiviGPS 
                  ? 'Le suivi GPS et Maps est activé. Les positions des livreurs sont visibles en temps réel.'
                  : 'Le suivi GPS et Maps est désactivé. Les livreurs fonctionnent sans révéler leur position (économies Maps).'}
              </p>
            </div>
            <button
              onClick={handleToggleLivraisonSuiviGPS}
              disabled={savingToggle}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                livraisonSuiviGPS ? 'bg-blue-600' : 'bg-gray-300'
              } ${savingToggle ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              title={livraisonSuiviGPS ? 'Désactiver le suivi GPS/Maps' : 'Activer le suivi GPS/Maps'}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  livraisonSuiviGPS ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {!livraisonSuiviGPS && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-yellow-800">
                  <strong>Mode sans Maps activé :</strong> Les livreurs fonctionnent avec des statuts textuels uniquement. 
                  Aucune position GPS n'est stockée ni affichée. Aucun coût Maps n'est appliqué.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-800 px-4 py-3 rounded">
          {successMessage}
        </div>
      )}

      {/* Barre de recherche et bouton carte */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Rechercher un livreur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        {livraisonSuiviGPS && livreurs.length > 0 && (
          <button
            onClick={() => setShowMap(!showMap)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              showMap
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Map className="w-4 h-4" />
            {showMap ? 'Masquer la carte' : 'Voir sur la carte'}
          </button>
        )}
      </div>

      {/* Carte en temps réel */}
      {showMap && livraisonSuiviGPS && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Suivi en temps réel</h3>
          <LivreursMap
            livreurs={livreurs}
            livraisonSuiviGPS={livraisonSuiviGPS}
            height="500px"
          />
        </div>
      )}

      {/* Liste des livreurs */}
      {filteredLivreurs.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Truck className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun livreur</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Aucun livreur ne correspond à votre recherche.' : 'Commencez par ajouter un livreur.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Livreur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Véhicule
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Compte PWA
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  {livraisonSuiviGPS && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Position GPS
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLivreurs.map((livreur) => (
                  <tr key={livreur.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {livreur.photo ? (
                            <img
                              src={livreur.photo}
                              alt={`${livreur.nom} ${livreur.prenom}`}
                              className="h-10 w-10 rounded-full object-cover border-2 border-gray-200"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center ${livreur.photo ? 'hidden' : ''}`}
                          >
                            <Truck className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {livreur.nom} {livreur.prenom}
                          </div>
                          {livreur.noteMoyenne > 0 && (
                            <div className="flex items-center gap-1 mt-1">
                              <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                              <span className="text-xs text-gray-600">
                                {livreur.noteMoyenne.toFixed(1)} ({livreur.nombreAvis || 0})
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {livreur.email && (
                        <div className="text-sm text-gray-900 flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {livreur.email}
                        </div>
                      )}
                      {livreur.telephone && (
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {livreur.telephone}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{livreur.vehicule || 'Non défini'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                            livreur.compteActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {livreur.compteActive ? 'Activé' : 'En attente'}
                        </span>
                        {livreur.derniereConnexion && (
                          <div className="text-xs text-gray-500">
                            Conn. {new Date(livreur.derniereConnexion).toLocaleDateString('fr-FR')}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(livreur.statut, livreur.disponible, livreur.actif)}`}>
                        {getStatusIcon(livreur.statut, livreur.disponible, livreur.actif)}
                        <span className="ml-1">
                          {getStatusLabel(livreur.statut, livreur.disponible, livreur.actif)}
                        </span>
                      </span>
                    </td>
                    {livraisonSuiviGPS && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {livreur.positionLatitude && livreur.positionLongitude ? (
                          <div className="text-xs text-gray-600">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              <span>
                                {livreur.positionLatitude.toFixed(4)}, {livreur.positionLongitude.toFixed(4)}
                              </span>
                            </div>
                            {livreur.dernierePosition && (
                              <div className="text-gray-500 mt-1">
                                {new Date(livreur.dernierePosition).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Non disponible</span>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(livreur)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(livreur)}
                          className="text-red-600 hover:text-red-900"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal enrichi */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedLivreur(null);
          resetForm();
        }}
        title={selectedLivreur ? 'Modifier le livreur' : 'Nouveau livreur'}
        maxWidth="max-w-3xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informations personnelles */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Informations personnelles</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prénom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  required
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="06 12 34 56 78"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="livreur@example.com"
                />
                {!selectedLivreur && (
                  <p className="text-xs text-gray-500 mt-1">
                    Un email d&apos;activation PWA sera envoyé automatiquement. Aucun mot de passe admin.
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <LivreurPhotoUpload
                photoUrl={formData.photo || ''}
                onPhotoChange={(url) => setFormData({ ...formData, photo: url })}
                commercantId={commercantId}
              />
            </div>
          </div>

          {/* Informations livreur */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Informations livreur</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Véhicule</label>
                <select
                  value={formData.vehicule || ''}
                  onChange={(e) => setFormData({ ...formData, vehicule: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  {getVehiculeOptions().map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <select
                  value={formData.statut}
                  onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  disabled={!selectedLivreur} // Statut seulement modifiable pour les livreurs existants
                >
                  {getStatutOptions().map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {livraisonSuiviGPS && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rayon maximum (km)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={formData.rayonMaxKm || ''}
                  onChange={(e) => setFormData({ ...formData, rayonMaxKm: e.target.value ? parseFloat(e.target.value) : null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ex: 10"
                />
                <p className="text-xs text-gray-500 mt-1">Distance maximale de livraison pour ce livreur</p>
              </div>
            )}

            <div className="flex items-center space-x-6 mt-4">
              {!selectedLivreur && (
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.actif}
                    onChange={(e) => setFormData({ ...formData, actif: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Livreur actif</span>
                </label>
              )}
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.disponible}
                  onChange={(e) => setFormData({ ...formData, disponible: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Disponible</span>
              </label>
            </div>
          </div>

          {!livraisonSuiviGPS && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-800">
                  Le suivi GPS/Maps est désactivé pour ce commerçant. Les positions GPS ne seront pas enregistrées ni affichées.
                </p>
              </div>
            </div>
          )}

          {selectedLivreur && (
            <LivreurAccountPanel
              livreur={selectedLivreur}
              allLivreurs={livreurs}
              onUpdated={async () => {
                await fetchLivreurs();
                const fresh = await apiService.getLivreur(selectedLivreur.id);
                setSelectedLivreur(fresh);
              }}
              onError={(msg) => setError(msg)}
            />
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => {
                setShowModal(false);
                setSelectedLivreur(null);
                resetForm();
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {selectedLivreur ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default LivreursManager;
